import { useEffect, useState } from 'react'
import { api, type TokenQuota } from '../services/api'
import { MINUS_LIMIT_MIO, formatMioTokens, tokenAccountView } from '../utils/tokenAccount'
import { forbiddenText } from '../lib/forbiddenText'
import '../pages/Dashboard.css'

// Einstellungen > Meine Tokens: das Token-Konto aus ERPNext (Typ 2, nur IT-SCE-Token), dieselbe
// Quelle und Darstellung wie im Dashboard. Ersetzt das alte Aria-Workspace-Quota-Widget (Typ 1)
// samt Kaufkacheln und "BASE" (ADR-Cockpit-Live-Daten-und-Token-Einheit, Entscheidung 2:
// "es gibt nur IT-SCE-Token"). Preise/Aufladung folgen mit dem Zielbild Verbrauchsabrechnung.

export default function TokenAccountCard({ slug }: { slug: string }) {
  const [quota, setQuota] = useState<TokenQuota | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'unavailable'>('loading')
  const [forbidden, setForbidden] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.getOrgTokenQuota(slug)
      .then(q => { if (!cancelled) { setQuota(q); setState('ok') } })
      .catch(e => { if (!cancelled) { setForbidden(forbiddenText(e)); setState('unavailable') } })
    return () => { cancelled = true }
  }, [slug])

  if (state === 'loading') return null

  if (state === 'unavailable' || !quota) {
    return (
      <section className="aria-settings-card" aria-label="Token-Konto">
        <h2>Token-Konto</h2>
        <p className="aria-settings-note" role="status">{forbidden ?? 'Das Token-Konto ist derzeit nicht abrufbar.'}</p>
      </section>
    )
  }

  const account = tokenAccountView(quota.verbleibend_mio, quota.gesamt_mio)
  const isDebit = account.mode === 'debit'

  return (
    <section className="aria-settings-card" aria-label="Token-Konto">
      <h2>Token-Konto ({quota.name})</h2>
      <p className="aria-settings-subtitle">
        Alle Aria-Dienste (Mail, Web, Storage, ERP, …) buchen auf dieses Konto. Einheit: IT-SCE-Token.
      </p>
      <div className={`token-quota-row${isDebit ? ' token-quota-row-debit' : ''}`} style={{ border: 'none', padding: 0 }}>
        <div className="token-quota-header">
          <span className="token-quota-label">Kontostand</span>
          <span className="token-quota-value">
            {isDebit
              ? `−${formatMioTokens(account.balanceMio)}`
              : `${formatMioTokens(account.balanceMio)} verfügbar`}
          </span>
        </div>
        {(isDebit || quota.gesamt_mio > 0) && (
          <div className="token-quota-bar-track">
            <div className="token-quota-bar-fill" style={{ width: `${account.fillPercent.toFixed(1)}%` }} />
          </div>
        )}
        {isDebit && (
          <div className="token-quota-scale" aria-hidden="true">
            <span>−{MINUS_LIMIT_MIO} Mio Token</span>
            <span>0</span>
          </div>
        )}
        <p className="aria-settings-note">
          Verbraucht {formatMioTokens(quota.verbraucht_mio)} · Verlauf siehe Token-Verlauf weiter unten
        </p>
        <p className="aria-settings-note">
          Aufladung und Fragen zum Konto: über den Support im Cockpit oder Ihren IT-SCE-Ansprechpartner.
        </p>
      </div>
    </section>
  )
}
